import { DOCUMENT } from "@angular/common";
import { Inject, Injectable } from "@angular/core";
import { getKey } from "pax-ui/core";
import { environment } from "src/environments/environment";

import { InvalidOperationException } from "../../exceptions/exceptions";
import {
  DataAnalytics,
  FunnelStepNumber,
  Page,
} from "../../routes/shared/shared-route.constant";
import { DocumentObjectModelService } from "../document-object-model.service";
import { LanguageService } from "../language/language.service";

/**
 * The tracking specification which has been used as a baseline for this implementation can be found here:
 * @see https://wiki.pax.ch/pages/viewpage.action?pageId=143296631
 */
@Injectable({ providedIn: "root" })
export class DataAnalyticsEventPublisherService {
  /**
   * This buffer acts as a temporary storage until the lazy loaded cookie script has been loaded.
   * Once the script has been loaded those events are added to the {@link window.dataLayer} when the next event is being added.
   *
   * Note: Certain user will block Google Analytics which will result in the data layer to never be loaded.
   * This could be considered a memory leak but for now we are not adding any mitigation.
   * We later might want to do something with that information.
   */
  private readonly _dataLayerBuffer: DataAnalyticsEvent[] = [];
  private readonly _window: Window;

  private _hasBufferBeenMerged = false;

  constructor(
    @Inject(DOCUMENT) private readonly _document: Document,
    private readonly _languageService: LanguageService,
    private readonly _documentObjectModelService: DocumentObjectModelService,
  ) {
    if (!this._document.defaultView) {
      throw new InvalidOperationException(
        "Was not able to find a global window object. This could be a side effect when changing to server side rendering (SSR)",
      );
    }

    this._window = this._document.defaultView;
    this.tryToSynchronizeBufferWithDataLayer();
  }

  /**
   * Event which is triggered whenever any type of navigation happens.
   */
  pushVirtualPageViewEvent(relativeUrlPathAfterRedirects: string) {
    const pageLocation = this.getUrl(relativeUrlPathAfterRedirects);

    const virtualPageViewEvent: DataAnalyticsVirtualPageViewEvent = {
      event: "virtual_page_view",
      application_name: "pv-b2c-sales",
      platform_env: environment.environment,
      traffic_type: "external", // There is currently no easy way how we can distinguish between internal and external traffic
      page_referrer: this.tryGetReferrer(),
      page_type: "funnel",
      page_language: this._languageService.getLanguage(),
      page_title: this._documentObjectModelService.getHtmlTitle(),
      page_location: pageLocation.toString(),
      page_path: this.getPagePath(pageLocation),
      page_path_global: this.getGlobalPagePath(pageLocation),
      content_group: "Todesfallversicherung",
      user_login_status: false,
      user_login_type: "pv-customer",
      user_id: undefined,
    };

    this.pushEvent(virtualPageViewEvent);
  }

  /**
   * Event which is triggered whenever the user navigates to a page which is part of one of our funnels.
   */
  pushFunnelViewEvent(
    page: Page,
    funnelStepNumber: FunnelStepNumber | undefined,
  ) {
    if (!funnelStepNumber) {
      throw new InvalidOperationException(
        `The page '${page}' requires a '${getKey<DataAnalytics>("funnelStepNumber")}' when trying to send a a funnel view event`,
      );
    }

    const funnelViewEvent: DataAnalyticsFunnelViewEvent = {
      event: "funnel_view",
      funnel_name: "todesfall",
      funnel_step: page,
      funnel_step_no: funnelStepNumber,
    };

    this.pushEvent(funnelViewEvent);
  }

  /**
   * Event which is triggered whenever the user downloads a document which we would like to track.
   */
  pushFileDownloadEvent(filenameWithExtension: string) {
    const fileExtension = this.getFileExtension(filenameWithExtension);

    const fileDownloadEvent: DataAnalyticsFileDownloadEvent = {
      event: "file_download",
      file_extension: fileExtension,
      file_name: filenameWithExtension,
      file_internal_reference: undefined,
      file_url: undefined,
    };

    this.pushEvent(fileDownloadEvent);
  }

  private pushEvent(dataAnalyticsEvent: DataAnalyticsEvent) {
    if (this.hasDataLayerBeenLoaded(this._window.dataLayer)) {
      this.pushEventToDataLayer(this._window.dataLayer, dataAnalyticsEvent);
    } else {
      this.pushEventToBuffer(dataAnalyticsEvent);
    }
  }

  private pushEventToDataLayer(
    dataLayerDataAnalyticsEvents: DataAnalyticsEvent[],
    dataAnalyticsEvent: DataAnalyticsEvent,
  ) {
    /**
     * We first add the events which have been published while the {@link window.dataLayer} has not been initialized yet.
     */
    if (!this._hasBufferBeenMerged) {
      this.mergeBufferWithDataLayer(dataLayerDataAnalyticsEvents);
    }

    dataLayerDataAnalyticsEvents.push(dataAnalyticsEvent);
  }

  private mergeBufferWithDataLayer(
    dataLayerDataAnalyticsEvents: DataAnalyticsEvent[],
  ) {
    dataLayerDataAnalyticsEvents.push(...this._dataLayerBuffer);
    this._hasBufferBeenMerged = true;
  }

  private pushEventToBuffer(dataAnalyticsEvent: DataAnalyticsEvent) {
    this._dataLayerBuffer.push(dataAnalyticsEvent);
  }

  private hasDataLayerBeenLoaded(
    dataLayerEvents: DataAnalyticsEvent[] | undefined,
  ): dataLayerEvents is DataAnalyticsEvent[] {
    return dataLayerEvents !== undefined;
  }

  private tryGetReferrer() {
    return this._document.referrer !== "" ? this._document.referrer : undefined;
  }

  private getUrl(relativeUrlPathWithoutLanguage: string) {
    /**
     * We do need get the language prefix as part of the event from Angular {@see relativeUrlPathAfterRedirects}.
     * As workaround we are using the the language from 'window.location'.
     */
    const language = this._languageService.getLanguage();

    /**
     * We also extract the origin from 'document.location'.
     * @example: https://checkout.pax.ch
     *
     * This should always remain the same across our application, so it's safe to do so.
     * Everything else is likely to conflict with {@see relativeUrlPathAfterRedirects}.
     */
    const origin = new URL(this._document.location.href).origin;
    const url = new URL(
      `${origin}/${language}${relativeUrlPathWithoutLanguage}`,
    );

    return url;
  }

  private getPagePath(pageLocation: URL) {
    return pageLocation.pathname;
  }

  private getGlobalPagePath(pageLocation: URL) {
    const pagePath = this.getPagePath(pageLocation);

    const match = LANGUAGE_AGNOSTIC_URL_PATH_REGEX.exec(pagePath);

    if (match) {
      // This ensures that 'http://localhost:4200/de' and 'http://localhost:4200/de/' return the same page path.
      // A URL with the value 'http://localhost:4200/de' (without a trailing slash) is otherwise not being matched by our regex.
      return match[1] ? match[1] : "/";
    }

    throw new InvalidOperationException(
      `Could not extract a language agnostic path from the url path '${pagePath}' with the regex '${String(LANGUAGE_AGNOSTIC_URL_PATH_REGEX)}'`,
    );
  }

  private getFileExtension(filenameWithExtension: string) {
    const fileExtension = filenameWithExtension.split(".").pop();

    if (fileExtension === undefined) {
      throw new InvalidOperationException(
        `Was not able to extract the file extension for the file '${filenameWithExtension}'`,
      );
    }

    return fileExtension;
  }

  /**
   * Without an explicit synchronization, the merge from our buffer with the data layer only happens when we push at least one event
   * AFTER the data layer has already been loaded. This can cause a problem in the following scenario:
   *
   * ------------------------------------------------------------------------------------------------------------------
   * Scenario (without synchronization):
   * ------------------------------------------------------------------------------------------------------------------
   * The User has declined the application in Skribble and is being redirected to our /decline page.
   * The user flow ends with this page and as a result no additional events are being pushed to the data layer.
   * The user then closes the tab.
   *
   * RESULT: The events which were added to the buffer before the data layer has been loaded are always lost.
   * ------------------------------------------------------------------------------------------------------------------
   *
   * ------------------------------------------------------------------------------------------------------------------
   * Scenario (with synchronization):
   * ------------------------------------------------------------------------------------------------------------------
   * The User has declined the application in Skribble and is being redirected to our /decline page.
   * The user flow ends with this page and as a result no additional events are being pushed to the data layer.
   * The user then closes the tab.
   *
   * RESULT: Due to the explicit synchronization, we are able to synchronize the buffer with the data layer before the user could close the tab.
   * This gives the tracking libraries a 'chance' to persist the events.
   * Only a 'chance' due to the fat that there are many different endpoints which are used as part of the external tracking script (e.g DoubleClick, GoogleTagManager, Facebook, .. ) and they are very cryptic in what an when they are transferring data.
   * ------------------------------------------------------------------------------------------------------------------
   */
  private tryToSynchronizeBufferWithDataLayer() {
    const interval = setInterval(() => {
      if (
        !this._hasBufferBeenMerged &&
        this.hasDataLayerBeenLoaded(this._window.dataLayer)
      ) {
        this.mergeBufferWithDataLayer(this._window.dataLayer);
        clearInterval(interval);
      }
    }, BUFFER_TO_DATA_LAYER_SYNCHRONIZATION_INTERVAL_IN_MILLISECONDS);
  }
}

// https://regex101.com/r/1K1hB7/3
const LANGUAGE_AGNOSTIC_URL_PATH_REGEX = /^\/\w{2}(?<languageAgnosticPath>.*)/;

const BUFFER_TO_DATA_LAYER_SYNCHRONIZATION_INTERVAL_IN_MILLISECONDS = 20;
